import { Logger as LoggerInterface, LogContext } from '../types';

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: LogContext;
  error?: Error;
}

export class Logger implements LoggerInterface {
  private logLevel: LogLevel;

  constructor(level: string = 'info') {
    this.logLevel = this.parseLogLevel(level);
    
    // Configure console output for different environments
    if (process.env.NODE_ENV === 'production') {
      // In production, use structured JSON logging
      this.setupProductionLogging();
    }
  }

  private parseLogLevel(level: string): LogLevel {
    switch (level.toLowerCase()) {
      case 'debug': return LogLevel.DEBUG;
      case 'info': return LogLevel.INFO;
      case 'warn': return LogLevel.WARN;
      case 'error': return LogLevel.ERROR;
      default: return LogLevel.INFO;
    }
  }

  private setupProductionLogging() {
    // Override console methods for structured logging in production
    const originalLog = console.log;
    const originalWarn = console.warn;
    const originalError = console.error;

    console.log = (...args) => {
      if (typeof args[0] === 'object' && args[0].level !== undefined) {
        originalLog(JSON.stringify(args[0]));
      } else {
        originalLog(...args);
      }
    };

    console.warn = (...args) => {
      if (typeof args[0] === 'object' && args[0].level !== undefined) {
        originalWarn(JSON.stringify(args[0]));
      } else {
        originalWarn(...args);
      }
    };

    console.error = (...args) => {
      if (typeof args[0] === 'object' && args[0].level !== undefined) {
        originalError(JSON.stringify(args[0]));
      } else {
        originalError(...args);
      }
    };
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel;
  }

  private createLogEntry(level: LogLevel, message: string, context?: LogContext, error?: Error): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      context: context ? this.sanitizeContext(context) : undefined,
      error: error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } as any : undefined
    };
  }

  private sanitizeContext(context: LogContext): LogContext {
    const sanitized = { ...context };
    
    // Remove sensitive information
    const sensitiveKeys = ['password', 'token', 'key', 'secret', 'apikey'];
    
    Object.keys(sanitized).forEach(key => {
      if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive))) {
        sanitized[key] = '[REDACTED]';
      }
    });

    return sanitized;
  }

  private formatLogEntry(entry: LogEntry): string {
    const levelNames = {
      [LogLevel.DEBUG]: 'DEBUG',
      [LogLevel.INFO]: 'INFO',
      [LogLevel.WARN]: 'WARN',
      [LogLevel.ERROR]: 'ERROR'
    };

    let formatted = `[${entry.timestamp}] ${levelNames[entry.level]}: ${entry.message}`;
    
    if (entry.context && Object.keys(entry.context).length > 0) {
      formatted += ` | Context: ${JSON.stringify(entry.context)}`;
    }
    
    if (entry.error) {
      formatted += ` | Error: ${entry.error.message}`;
      if (entry.error.stack && this.logLevel === LogLevel.DEBUG) {
        formatted += `\nStack: ${entry.error.stack}`;
      }
    }

    return formatted;
  }

  private output(entry: LogEntry) {
    if (process.env.NODE_ENV === 'production') {
      // Structured logging for production
      const structuredLog = {
        timestamp: entry.timestamp,
        level: entry.level,
        message: entry.message,
        ...entry.context,
        error: entry.error
      };

      switch (entry.level) {
        case LogLevel.ERROR:
          console.error(structuredLog);
          break;
        case LogLevel.WARN:
          console.warn(structuredLog);
          break;
        default:
          console.log(structuredLog);
      }
    } else {
      // Human-readable logging for development
      const formatted = this.formatLogEntry(entry);
      
      switch (entry.level) {
        case LogLevel.ERROR:
          console.error(`\x1b[31m${formatted}\x1b[0m`); // Red
          break;
        case LogLevel.WARN:
          console.warn(`\x1b[33m${formatted}\x1b[0m`); // Yellow
          break;
        case LogLevel.INFO:
          console.log(`\x1b[36m${formatted}\x1b[0m`); // Cyan
          break;
        case LogLevel.DEBUG:
          console.log(`\x1b[90m${formatted}\x1b[0m`); // Gray
          break;
      }
    }
  }

  debug(message: string, context?: LogContext) {
    if (this.shouldLog(LogLevel.DEBUG)) {
      const entry = this.createLogEntry(LogLevel.DEBUG, message, context);
      this.output(entry);
    }
  }

  info(message: string, context?: LogContext) {
    if (this.shouldLog(LogLevel.INFO)) {
      const entry = this.createLogEntry(LogLevel.INFO, message, context);
      this.output(entry);
    }
  }

  warn(message: string, context?: LogContext) {
    if (this.shouldLog(LogLevel.WARN)) {
      const entry = this.createLogEntry(LogLevel.WARN, message, context);
      this.output(entry);
    }
  }

  error(message: string, contextOrError?: LogContext | Error, error?: Error) {
    if (this.shouldLog(LogLevel.ERROR)) {
      let context: LogContext | undefined;
      let actualError: Error | undefined;

      if (contextOrError instanceof Error) {
        actualError = contextOrError;
      } else {
        context = contextOrError;
        actualError = error;
      }

      const entry = this.createLogEntry(LogLevel.ERROR, message, context, actualError);
      this.output(entry);
    }
  }

  // Performance logging utility
  time(label: string, context?: LogContext): () => void {
    const startTime = Date.now();
    
    this.debug(`Timer started: ${label}`, context);
    
    return () => {
      const duration = Date.now() - startTime;
      this.info(`Timer ended: ${label}`, { ...context, duration });
    };
  }
}

// Create default logger instance
export const createLogger = (level?: string): Logger => {
  return new Logger(level || process.env.LOG_LEVEL || 'info');
};

// Default logger instance
export const defaultLogger = createLogger();

// Utility functions
export const isValidUrl = (string: string): boolean => {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
};

export const sanitizeInput = (input: string): string => {
  // Basic sanitization to prevent injection attacks
  return input
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocols
    .trim();
};

export const sleep = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

export const retry = async <T>(
  fn: () => Promise<T>,
  retries: number = 3,
  delay: number = 1000
): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0) {
      await sleep(delay);
      return retry(fn, retries - 1, delay * 2); // Exponential backoff
    }
    throw error;
  }
};

export const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

export const truncateText = (text: string, maxLength: number = 100): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};